(function () {
  'use strict';

  /**
   * Fallback default pack qty when `<quick-buy-pack-sizes>` has no `data-default-pack-quantity`.
   * Must match Liquid `quick_buy_default_pack_quantity` unless you omit the data attribute intentionally.
   */
  var QUICK_BUY_DEFAULT_PACK_QUANTITY = 3;

  /**
   * Suffix on the hidden line item property so a test theme's add-to-carts can be
   * told apart from the control theme in reporting. Comes from theme settings
   * ("AB Test" > "Quick Add Line Item Property Suffix") via `data-line-item-suffix` on
   */
  function packSizePropertySuffix(packRoot) {
    var raw = packRoot && packRoot.getAttribute ? packRoot.getAttribute('data-line-item-suffix') : null;
    return raw != null ? String(raw).trim() : '';
  }

  /** Successful variant rows keyed by numeric id (avoids reparsing liquid map). Omit misses so we retry after DOM updates. */
  var VARIANT_CACHE = {};

  function getDefaultPackQuantity(packRoot) {
    var raw =
      packRoot && packRoot.nodeType === 1
        ? packRoot.getAttribute('data-default-pack-quantity')
        : null;
    var n = raw != null && raw !== '' ? parseInt(raw, 10) : NaN;
    if (!isNaN(n) && n >= 1) return n;
    return QUICK_BUY_DEFAULT_PACK_QUANTITY;
  }

  function clearActivePackButtons(packRoot) {
    if (!packRoot) return;
    packRoot.querySelectorAll('.quick-buy-pack-sizes__button').forEach(function (b) {
      b.classList.remove('active');
    });
  }

  function setOnlyActivePackButton(packRoot, button) {
    if (!packRoot || !button) return;
    clearActivePackButtons(packRoot);
    button.classList.add('active');
  }

  /** Remove .active from hidden (excluded) pills so stale state doesn’t linger. */
  function stripActiveFromHiddenButtons(packRoot) {
    if (!packRoot) return;
    packRoot.querySelectorAll('.quick-buy-pack-sizes__button').forEach(function (b) {
      if (b.style.display === 'none') b.classList.remove('active');
    });
  }

  function formatMoneyFromCents(cents) {
    if (typeof Shopify === 'undefined' || !Shopify.formatMoney) {
      return String((cents || 0) / 100);
    }
    return Shopify.formatMoney(cents, '').replace('.00', '');
  }

  function attrToCents(val) {
    if (val == null || val === '') return null;
    var n = Number(val);
    if (isNaN(n)) return null;
    if (n === Math.floor(n) && Math.abs(n) >= 100) return Math.round(n);
    return Math.round(n * 100);
  }

  function getQuickBuyProductId(packRoot) {
    var drawer = packRoot.closest('.shop-grid-item__quick-buy');
    return drawer ? drawer.getAttribute('data-product-id') : null;
  }

  function getGridScopeForActiveSwatch(packRoot) {
    // Prefer this drawer's own card (data-qb-key is assigned by grid-item.js); the same
    // product can be rendered more than once on a page, so product id alone is ambiguous.
    var drawerForKey = packRoot.closest('.shop-grid-item__quick-buy');
    var instanceKey = drawerForKey ? drawerForKey.getAttribute('data-qb-key') : null;
    if (instanceKey) {
      var keyedScope = document.querySelector(
        '.shop-grid-item-v5[data-qb-key="' + instanceKey + '"]:not(.shop-grid-item-bottom)'
      );
      if (keyedScope) return keyedScope;
    }
    var pid = getQuickBuyProductId(packRoot);
    if (pid) {
      var scopes = document.querySelectorAll('.shop-grid-item-v5[data-product-id="' + pid + '"]');
      for (var i = 0; i < scopes.length; i++) {
        if (scopes[i].querySelector('.shop-grid-item-swatches:not(.shop-grid-item-swatches-all)')) return scopes[i];
      }
      return scopes[0] || null;
    }
    return packRoot.closest('.shop-grid-item-v5, .shop-grid-item-v2');
  }

  function getActiveSwatchAnchor(gridScope) {
    if (!gridScope) return null;
    var swatchScope = gridScope.querySelector('.shop-grid-item-swatches:not(.shop-grid-item-swatches-all)') || gridScope;
    var active = swatchScope.querySelector('.swatch-wrap.active a');
    return active;
  }

  function findSwatchAnchorForChildProductId(drawer, parentProductId, childProductId) {
    if (!childProductId) return null;
    var sel =
      '.shop-grid-item-swatches .swatch-wrap[data-prod-id="' + childProductId + '"] a:not(.shop-grid-item-swatch-count-link)';
    if (drawer) {
      var a = drawer.querySelector(sel);
      if (a) return a;
    }
    if (parentProductId) {
      var scopes = document.querySelectorAll('.shop-grid-item-v5[data-product-id="' + parentProductId + '"]');
      for (var i = 0; i < scopes.length; i++) {
        a = scopes[i].querySelector(sel);
        if (a) return a;
      }
    }
    return null;
  }

  function findSwatchForPackPricing(packRoot) {
    if (!packRoot) return null;
    var drawer = packRoot.closest('.shop-grid-item__quick-buy');
    if (drawer) {
      var form = packRoot.closest('form');
      var item = form && form.closest('.shop-grid-item__quick-buy-item');
      var ref = item && item.getAttribute('data-ref');
      var pid = drawer.getAttribute('data-product-id') || '';
      if (ref) {
        var linked = findSwatchAnchorForChildProductId(drawer, pid, ref);
        if (linked) return linked;
      }
    }
    var gridScope = getGridScopeForActiveSwatch(packRoot);
    return getActiveSwatchAnchor(gridScope);
  }

  /**
   * Pack line prices for qty `q` from a unit price. Shared by the ATC label and the pill price line:
   * compare-at sale -> unit x q with compare x q struck; pack button price -> that price with unit x q struck.
   */
  function computePackLinePrices(unitCents, compareCents, q, buttonPriceAttr) {
    var hasCompareSale = compareCents != null && compareCents > unitCents;
    var displayPrice = '';
    var strikePrice = '';
    if (hasCompareSale) {
      displayPrice = formatMoneyFromCents(unitCents * q);
      strikePrice = formatMoneyFromCents(compareCents * q);
    } else if (buttonPriceAttr) {
      displayPrice = buttonPriceAttr;
      strikePrice = formatMoneyFromCents(unitCents * q);
    } else {
      displayPrice = formatMoneyFromCents(unitCents * q);
      strikePrice = '';
    }
    return { displayPrice: displayPrice, strikePrice: strikePrice, hasCompareSale: hasCompareSale };
  }

  /** Rich ATC label: formatted pack line price(s) • Pre-Order / Add to Cart. + discounted prices */
  function buildQuickBuyPackButtonLabelInnerHtml(swatchLink, quantity, buttonPriceAttr, actionLabel) {
    var q = Math.max(1, parseInt(quantity, 10) || 1);
    if (!swatchLink) {
      return '<span>' + actionLabel + '</span>';
    }
    var unitCents = attrToCents(swatchLink.getAttribute('data-price'));
    if (unitCents == null) {
      return '<span>' + actionLabel + '</span>';
    }
    var compareCents = attrToCents(swatchLink.getAttribute('data-compare-price'));
    var prices = computePackLinePrices(unitCents, compareCents, q, buttonPriceAttr);
    var displayPrice = prices.displayPrice;
    var strikePrice = prices.strikePrice;
    var sep = ' <span>•</span> ';
    // Whenever a price is struck through (compare-at sale, or pack price vs unit x qty), the
    // price paid is shown in orange, matching snippets/quick-buy-add-to-cart-label.liquid.
    if (strikePrice) {
      return (
        '<span><span class="money" style="display:inline-block;color:#FF965B;">' +
        displayPrice +
        '</span> <span style="text-decoration:line-through;">' +
        strikePrice +
        '</span></span>' +
        sep +
        '<span>' +
        actionLabel +
        '</span>'
      );
    }
    return '<span class="money">' + displayPrice + '</span>' + sep + '<span>' + actionLabel + '</span>';
  }

  /**
   * Pack Size Pricing / Discounts: priced from the active swatch.
   */
  function updatePackButtonPrices(packRoot) {
    if (!packRoot) return;
    var swatchLink = findSwatchForPackPricing(packRoot);
    if (!swatchLink) return;
    var unitCents = attrToCents(swatchLink.getAttribute('data-price'));
    if (unitCents == null) return;
    var compareCents = attrToCents(swatchLink.getAttribute('data-compare-price'));
    packRoot.querySelectorAll('.quick-buy-pack-sizes__button').forEach(function (btn) {
      var priceEl = btn.querySelector('.quick-buy-pack-sizes__price');
      if (!priceEl) return;
      var q = Math.max(1, parseInt(btn.getAttribute('data-pack-size-quantity'), 10) || 1);
      var prices = computePackLinePrices(unitCents, compareCents, q, btn.getAttribute('data-pack-size-button-price') || '');
      priceEl.innerHTML = prices.displayPrice + (prices.strikePrice ? ' <s>' + prices.strikePrice + '</s>' : '');
    });
  }

  function getQuickBuyForm(packRoot) {
    if (!packRoot) return null;
    return packRoot.closest('form');
  }

  function getFormVariantId(form) {
    if (!form) return null;
    var el = form.querySelector('.quick-buy-variant-id');
    if (!el) el = form.querySelector('input[name="id"][type="hidden"]');
    if (!el) el = form.querySelector('select[name="id"]');
    if (!el) return null;
    var n = parseInt(el.value, 10);
    return isNaN(n) ? null : n;
  }

  function normalizeVariantJsonEntry(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var sku = raw.sku != null && String(raw.sku).trim() !== '' ? String(raw.sku).trim() : '';
    var iq = raw.inventory_quantity;
    if (iq != null && typeof iq !== 'number') iq = parseInt(iq, 10);
    if (isNaN(iq)) iq = null;
    var im = raw.inventory_management;
    if (im === '' || im === false) im = null;
    if (im != null) im = String(im);
    return {
      sku: sku,
      inventory_quantity: typeof iq === 'number' ? iq : null,
      inventory_policy: raw.inventory_policy ? String(raw.inventory_policy) : '',
      inventory_management: im,
    };
  }

  function getParsedVariantIndexMap(packRoot) {
    if (!packRoot || packRoot.nodeType !== 1) return null;
    if (packRoot.__knickeyQbVariantIdxParsed !== undefined) return packRoot.__knickeyQbVariantIdxParsed;
    var el = packRoot.querySelector('script[type="application/json"][data-qb-variant-index]');
    if (!el || !el.textContent) {
      packRoot.__knickeyQbVariantIdxParsed = null;
      return null;
    }
    try {
      var map = JSON.parse(el.textContent.trim());
      packRoot.__knickeyQbVariantIdxParsed = map && typeof map === 'object' ? map : null;
      return packRoot.__knickeyQbVariantIdxParsed;
    } catch (err) {
      packRoot.__knickeyQbVariantIdxParsed = null;
      return null;
    }
  }

  function variantEntryFromPackRootLiquid(packRoot, variantId) {
    var map = getParsedVariantIndexMap(packRoot);
    if (!map) return null;
    var sid = String(variantId);
    var raw = map[sid];
    if (raw == null) raw = map[variantId];
    return normalizeVariantJsonEntry(raw);
  }

  /** SKU + inventory from Liquid JSON embedded in `<quick-buy-pack-sizes>` (no network). */
  function getVariantFields(variantId, packRoot) {
    var id = Number(variantId);
    if (!id) return Promise.resolve(null);
    var hit = VARIANT_CACHE[id];
    if (hit != null && typeof hit === 'object') return Promise.resolve(hit);
    var local = packRoot ? variantEntryFromPackRootLiquid(packRoot, id) : null;
    if (local) VARIANT_CACHE[id] = local;
    return Promise.resolve(local);
  }

  function getVariantSku(variantId, packRoot) {
    return getVariantFields(variantId, packRoot).then(function (e) {
      return e && e.sku ? e.sku : '';
    });
  }

  /** Mirrors PDP: only enforce caps when Shopify tracks inventory and policy is deny. */
  function packQtyAllowedForVariant(entry, packQty) {
    if (!entry) return true;
    var q = Math.max(1, parseInt(packQty, 10) || 1);
    if (!entry.inventory_management) return true;
    if (entry.inventory_policy !== 'deny') return true;
    var inv = entry.inventory_quantity;
    if (inv == null || typeof inv !== 'number') return true;
    return inv >= q;
  }

  function applyInventoryDisabledOnPackButtons(packRoot, entry) {
    packRoot.querySelectorAll('.quick-buy-pack-sizes__button').forEach(function (btn) {
      var pq = parseInt(btn.getAttribute('data-pack-size-quantity'), 10) || 1;
      btn.disabled = !packQtyAllowedForVariant(entry, pq);
    });
  }

  function pickBestEnabledPackButton(packRoot, preferredQty) {
    var vb = visibleButtons(packRoot).filter(function (b) {
      return !b.disabled;
    });
    if (!vb.length) return null;
    if (preferredQty != null && !isNaN(preferredQty)) {
      var match = vb.filter(function (b) {
        return (parseInt(b.getAttribute('data-pack-size-quantity'), 10) || 1) === preferredQty;
      })[0];
      if (match) return match;
    }
    var def = getDefaultPackQuantity(packRoot);
    var defMatch = vb.filter(function (b) {
      return (parseInt(b.getAttribute('data-pack-size-quantity'), 10) || 1) === def;
    })[0];
    if (defMatch) return defMatch;
    return vb
      .slice()
      .sort(function (a, b) {
        return (
          (parseInt(b.getAttribute('data-pack-size-quantity'), 10) || 1) -
          (parseInt(a.getAttribute('data-pack-size-quantity'), 10) || 1)
        );
      })[0];
  }

  function refreshPackAvailability(packRoot) {
    var form = getQuickBuyForm(packRoot);
    if (!form) return Promise.resolve();
    return getVariantFields(getFormVariantId(form), packRoot).then(function (entry) {
      applyPackDisplayExclusions(packRoot);
      applyInventoryDisabledOnPackButtons(packRoot, entry);
      updatePackButtonPrices(packRoot);
      var active = packRoot.querySelector('.quick-buy-pack-sizes__button.active');
      var invalid =
        !active ||
        active.style.display === 'none' ||
        active.disabled ||
        !packQtyAllowedForVariant(
          entry,
          parseInt(active.getAttribute('data-pack-size-quantity'), 10) || 1
        );
      if (!invalid) return;
      var drawer = packRoot.closest('.shop-grid-item__quick-buy');
      var raw = drawer && drawer.getAttribute('data-quick-buy-pack-qty');
      var storedN = raw != null && raw !== '' ? parseInt(raw, 10) : NaN;
      var pick = pickBestEnabledPackButton(packRoot, !isNaN(storedN) ? storedN : null);
      if (!pick) return;
      setOnlyActivePackButton(packRoot, pick);
      applySelection(packRoot, pick);
      if (drawer) {
        var newQ = parseInt(pick.getAttribute('data-pack-size-quantity'), 10) || 1;
        drawer.setAttribute('data-quick-buy-pack-qty', String(newQ));
        var bp = pick.getAttribute('data-pack-size-button-price') || '';
        if (bp) drawer.setAttribute('data-quick-buy-pack-button-price', bp);
        else drawer.removeAttribute('data-quick-buy-pack-button-price');
      }
    });
  }

  function quickBuyPackLineItemPropertyKey(variantId, sku, packRoot) {
    var keyPart = sku != null && String(sku).trim() !== '' ? String(sku).trim() : 'variant-' + variantId;
    return '_' + keyPart + ' - Quick Buy Pack Size Clicked' + packSizePropertySuffix(packRoot);
  }

  function removeQuickBuyPackLineItem(form) {
    if (!form) return;
    var existing = form.querySelector('input[data-knickey-quick-buy-pack-size-line-item]');
    if (existing) existing.remove();
  }

  function upsertQuickBuyPackLineItem(packRoot, form, quantity) {
    removeQuickBuyPackLineItem(form);
    if (!form) return Promise.resolve();
    var variantId = getFormVariantId(form);
    if (!variantId) return Promise.resolve();
    return getVariantSku(variantId, packRoot).then(function (sku) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.setAttribute('data-knickey-quick-buy-pack-size-line-item', '');
      input.name = 'properties[' + quickBuyPackLineItemPropertyKey(variantId, sku, packRoot) + ']';
      input.value = String(quantity);
      form.appendChild(input);
    });
  }

  function visibleButtons(packRoot) {
    return Array.prototype.slice.call(packRoot.querySelectorAll('.quick-buy-pack-sizes__button')).filter(function (btn) {
      return btn.style.display !== 'none';
    });
  }

  /**
   * After exclusions resolve, enforce exactly one visible .active pill.
   * If SSR/markup left 0 or 2+ actives visible, prefer theme default qty, then first visible.
   */
  function resolvePackActiveSingle(packRoot, opts) {
    opts = opts || {};

    stripActiveFromHiddenButtons(packRoot);
    var vb = visibleButtons(packRoot);
    if (!vb.length) return null;

    var visibleActive = vb.filter(function (b) {
      return b.classList.contains('active') && !b.disabled;
    });
    if (
      visibleActive.length === 1 &&
      !opts.forceDefault &&
      visibleActive[0].style.display !== 'none'
    ) {
      return visibleActive[0];
    }

    clearActivePackButtons(packRoot);

    var n = getDefaultPackQuantity(packRoot);
    var enabled = vb.filter(function (b) {
      return !b.disabled;
    });
    var preferred =
      enabled.filter(function (b) {
        return (parseInt(b.getAttribute('data-pack-size-quantity'), 10) || 1) === n;
      })[0] || enabled[0];
    if (!preferred) return null;
    preferred.classList.add('active');
    return preferred;
  }

  /** Toggle visibility for colour-specific exclusions only (does not choose .active). */
  function applyPackDisplayExclusions(packRoot) {
    var currentProductId = Number(packRoot.getAttribute('data-product-id') || 0);
    if (!currentProductId) return;

    var buttons = packRoot.querySelectorAll('.quick-buy-pack-sizes__button');
    buttons.forEach(function (btn) {
      var excludedProducts = btn.getAttribute('data-excluded-products');
      if (!excludedProducts) {
        btn.style.display = '';
        return;
      }
      var excludedIds = excludedProducts.split(',').map(function (id) {
        return Number(id.trim());
      });
      btn.style.display = excludedIds.indexOf(currentProductId) > -1 ? 'none' : '';
    });

    stripActiveFromHiddenButtons(packRoot);
  }

  function updateAddButton(packRoot, form, quantity, buttonPriceAttr) {
    if (!form) return;
    var button = form.querySelector('.shop-grid-item__quick-buy-add-to-cart-btn, .add-button');
    if (!button || button.disabled || button.classList.contains('unavailable')) return;

    if (!button.dataset.baseLabel) {
      button.dataset.baseLabel = button.textContent.trim();
    }
    var baseLabel = button.dataset.baseLabel;
    var actionLabel = baseLabel.toLowerCase().indexOf('pre-order') > -1 ? 'Pre-Order' : 'Add to Cart';
    var q = Math.max(1, parseInt(quantity, 10) || 1);

    var swatchLink = findSwatchForPackPricing(packRoot);
    button.innerHTML = buildQuickBuyPackButtonLabelInnerHtml(swatchLink, q, buttonPriceAttr, actionLabel);
  }

  function applySelection(packRoot, button) {
    if (!packRoot || !button) return;
    var form = getQuickBuyForm(packRoot);
    if (!form) return;

    var quantity = parseInt(button.getAttribute('data-pack-size-quantity'), 10) || 1;
    var buttonPrice = button.getAttribute('data-pack-size-button-price') || '';

    var quantityInput = form.querySelector('input[name="quantity"]');
    if (quantityInput) quantityInput.value = String(quantity);

    upsertQuickBuyPackLineItem(packRoot, form, quantity);
    updatePackButtonPrices(packRoot);
    updateAddButton(packRoot, form, quantity, buttonPrice);
  }

  function pickButtonForStoredQty(packRoot, targetQty) {
    applyPackDisplayExclusions(packRoot);
    var vb = visibleButtons(packRoot).filter(function (b) {
      return !b.disabled;
    });
    if (!vb.length) return null;
    var exact = vb.filter(function (b) {
      return (parseInt(b.getAttribute('data-pack-size-quantity'), 10) || 1) === targetQty;
    })[0];
    var btn = exact || vb[0];
    setOnlyActivePackButton(packRoot, btn);
    return btn;
  }

  /**
   * Re-apply stored pack choice across every colour’s quick-buy-pack-sizes inside the drawer.
   */
  function syncPackRootsInDrawer(drawer, targetQty) {
    if (!drawer || targetQty == null || isNaN(targetQty)) return Promise.resolve();

    var packRoots = drawer.querySelectorAll('quick-buy-pack-sizes');

    return Promise.all(
      Array.prototype.map.call(packRoots, function (root) {
        var form = getQuickBuyForm(root);
        if (!form) return Promise.resolve(null);
        return getVariantFields(getFormVariantId(form), root).then(function (entry) {
          applyPackDisplayExclusions(root);
          applyInventoryDisabledOnPackButtons(root, entry);
          var btn = pickButtonForStoredQty(root, targetQty);
          if (btn) applySelection(root, btn);
          return btn;
        });
      })
    ).then(function (btns) {
      var firstBtn = null;
      for (var i = 0; i < btns.length; i++) {
        if (btns[i]) {
          firstBtn = btns[i];
          break;
        }
      }
      if (drawer && firstBtn) {
        var appliedQty = parseInt(firstBtn.getAttribute('data-pack-size-quantity'), 10) || 1;
        if (appliedQty !== targetQty) {
          drawer.setAttribute('data-quick-buy-pack-qty', String(appliedQty));
          var refBpDrawer = firstBtn.getAttribute('data-pack-size-button-price') || '';
          if (refBpDrawer) drawer.setAttribute('data-quick-buy-pack-button-price', refBpDrawer);
          else drawer.removeAttribute('data-quick-buy-pack-button-price');
        }
      }
    });
  }

  function initializeSinglePackRootStandalone(packRoot) {
    var form = getQuickBuyForm(packRoot);
    if (!form) return Promise.resolve();
    return getVariantFields(getFormVariantId(form), packRoot).then(function (entry) {
      applyPackDisplayExclusions(packRoot);
      applyInventoryDisabledOnPackButtons(packRoot, entry);
      var activeButton = resolvePackActiveSingle(packRoot, {});
      if (activeButton) {
        applySelection(packRoot, activeButton);
        return;
      }
      var pick = pickBestEnabledPackButton(packRoot, null);
      if (pick) {
        setOnlyActivePackButton(packRoot, pick);
        applySelection(packRoot, pick);
      }
    });
  }

  window.refreshQuickBuyPackAvailabilityForQuickBuyForm = function (formEl) {
    if (!formEl || !formEl.querySelector) return Promise.resolve();
    var packRoot = formEl.querySelector('quick-buy-pack-sizes');
    return packRoot ? refreshPackAvailability(packRoot) : Promise.resolve();
  };

  window.syncQuickBuyPackForProductCard = function (drawer) {
    if (!drawer) return Promise.resolve();
    var stored = drawer.getAttribute('data-quick-buy-pack-qty');
    var targetQty = stored != null && stored !== '' ? parseInt(stored, 10) : null;

    var packRoots = drawer.querySelectorAll('quick-buy-pack-sizes');
    if (!packRoots.length) return Promise.resolve();

    if (targetQty != null && !isNaN(targetQty)) {
      return syncPackRootsInDrawer(drawer, targetQty);
    }

    return Promise.all(
      Array.prototype.map.call(packRoots, function (root) {
        return initializeSinglePackRootStandalone(root);
      })
    );
  };

  function clickedPackButton(target) {
    if (!target || !target.closest) return null;
    var el = target.nodeType === 1 ? target : target.parentElement;
    return el && el.closest('.quick-buy-pack-sizes__button');
  }

  document.addEventListener(
    'click',
    function (event) {
      var button = clickedPackButton(event.target);
      if (!button || button.style.display === 'none' || button.disabled) return;
      event.preventDefault();

      var packRoot = button.closest('quick-buy-pack-sizes');
      if (!packRoot) return;

      var drawer = packRoot.closest('.shop-grid-item__quick-buy');
      var qty = parseInt(button.getAttribute('data-pack-size-quantity'), 10) || 1;
      var bp = button.getAttribute('data-pack-size-button-price') || '';

      if (drawer) {
        drawer.setAttribute('data-quick-buy-pack-qty', String(qty));
        if (bp) drawer.setAttribute('data-quick-buy-pack-button-price', bp);
        else drawer.removeAttribute('data-quick-buy-pack-button-price');
        syncPackRootsInDrawer(drawer, qty);
      } else {
        setOnlyActivePackButton(packRoot, button);
        applySelection(packRoot, button);
      }
    },
    true
  );

  window.initializeQuickBuyPackSizes = function () {
    var seen = {};
    document.querySelectorAll('.shop-grid-item__quick-buy').forEach(function (drawer) {
      var pid = drawer.getAttribute('data-product-id') || '';
      // Key by card instance when available so a product rendered twice gets both drawers synced
      var key = drawer.getAttribute('data-qb-key') || pid || drawer.id || String(Math.random());
      if (seen[key]) return;
      seen[key] = true;
      window.syncQuickBuyPackForProductCard(drawer);
    });

    document.querySelectorAll('quick-buy-pack-sizes').forEach(function (root) {
      if (root.closest('.shop-grid-item__quick-buy')) return;
      initializeSinglePackRootStandalone(root);
    });
  };

  document.addEventListener(
    'change',
    function (e) {
      var t = e.target;
      if (!t || t.name !== 'quick-buy-variant') return;
      if (!t.matches || !t.matches('input.size-radio')) return;
      var form = t.closest('form');
      if (!form || !form.closest('.shop-grid-item__quick-buy')) return;
      var packRoot = form.querySelector('quick-buy-pack-sizes');
      if (packRoot) refreshPackAvailability(packRoot);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.initializeQuickBuyPackSizes);
  } else {
    window.initializeQuickBuyPackSizes();
  }
})();
